import Links from '@/shared/constants/navegacao';
import { Form, Spinner, Table } from 'react-bootstrap';
import { ReadPersonDto } from '@/api.types';
import { readPerson } from '../../person.service';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBreadcrumbActions } from '@/shared/contexts/BreadcrumbActionsContext';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import imprimirDiv from '@/shared/utils/impressao';
import {
  AniversarioSortConfig,
  AniversarioSortKey,
  MESES,
  diaAniversario,
  idadeAnosLabel,
  linhasExcelAniversariantes,
  nomeArquivoAniversarios,
  ordenarAniversariantes,
  partirEmDuasColunas,
  perfisAtivosLabels,
  proximaOrdenacao,
  classeLinhaAniversario,
  statusAniversarioNoMes,
  FILTROS_PERFIL_ANIVERSARIO_PADRAO,
  FiltrosPerfilAniversario,
  OPCOES_FILTRO_PERFIL_ANIVERSARIO,
  filtrarAniversariantesPorPerfil,
} from './aniversarios.utils';

const CONTEUDO_ID = 'aniversarios-conteudo';

const COLUNAS: { key: AniversarioSortKey; label: string; className?: string; style?: React.CSSProperties }[] = [
  { key: 'dia', label: 'Dia', className: 'text-center', style: { width: '3.5rem' } },
  { key: 'nome', label: 'Nome' },
  { key: 'idade', label: 'Idade', className: 'text-center', style: { width: '4rem' } },
  { key: 'perfis', label: 'Perfis' },
];

function exportarAniversariosExcel(pessoas: ReadPersonDto[], mes: number) {
  const worksheetData = [
    ['Dia', 'Nome', 'Idade', 'Perfis'],
    ...linhasExcelAniversariantes(pessoas),
  ];
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Aniversariantes');
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), nomeArquivoAniversarios(mes));
}

type TabelaProps = {
  pessoas: ReadPersonDto[];
  mesSelecionado: number;
  sortConfig: AniversarioSortConfig;
  onSort: (key: AniversarioSortKey) => void;
};

function AniversariosTabela({ pessoas, mesSelecionado, sortConfig, onSort }: TabelaProps) {
  if (pessoas.length === 0) return null;

  return (
    <Table striped bordered hover size="sm" responsive className="mb-0">
      <thead>
        <tr>
          {COLUNAS.map((col) => (
            <th
              key={col.key}
              className={col.className}
              style={{ ...col.style, cursor: 'pointer', userSelect: 'none' }}
              onClick={() => onSort(col.key)}
              title="Ordenar"
            >
              {col.label}{' '}
              {sortConfig.key === col.key && (
                <span className="d-print-none">
                  {sortConfig.direction === 'ASC' ? '▲' : '▼'}
                </span>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {pessoas.map((pessoa) => {
          const perfis = perfisAtivosLabels(pessoa);
          const linhaClass = classeLinhaAniversario(
            statusAniversarioNoMes(pessoa.dataNascimento, mesSelecionado),
          );
          return (
            <tr key={pessoa.id} className={linhaClass}>
              <td className="text-center">
                {diaAniversario(pessoa.dataNascimento)}
              </td>
              <td className="text-break">{pessoa.nome}</td>
              <td className="text-center">{idadeAnosLabel(pessoa.dataNascimento)}</td>
              <td>{perfis.length > 0 ? perfis.join(', ') : '-'}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
}

export default function AniversariosPage() {
  const { setActions } = useBreadcrumbActions();
  const [mes, setMes] = useState(() => new Date().getMonth() + 1);
  const [pessoas, setPessoas] = useState<ReadPersonDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [sortConfig, setSortConfig] = useState<AniversarioSortConfig>({
    key: 'dia',
    direction: 'ASC',
  });
  const [filtrosPerfil, setFiltrosPerfil] = useState<FiltrosPerfilAniversario>(
    () => ({ ...FILTROS_PERFIL_ANIVERSARIO_PADRAO }),
  );

  const nomeMes = MESES.find((m) => m.value === mes)?.label ?? '';

  const pessoasFiltradas = useMemo(
    () => filtrarAniversariantesPorPerfil(pessoas, filtrosPerfil),
    [pessoas, filtrosPerfil],
  );

  const pessoasOrdenadas = useMemo(
    () => ordenarAniversariantes(pessoasFiltradas, sortConfig),
    [pessoasFiltradas, sortConfig],
  );

  const [colunaEsquerda, colunaDireita] = useMemo(
    () => partirEmDuasColunas(pessoasOrdenadas),
    [pessoasOrdenadas],
  );

  const handleImprimir = useCallback(() => {
    imprimirDiv(CONTEUDO_ID, {
      titulo: 'Aniversariantes',
      subtitulo: nomeMes,
    });
  }, [nomeMes]);

  const handleExportarExcel = useCallback(() => {
    exportarAniversariosExcel(pessoasOrdenadas, mes);
  }, [pessoasOrdenadas, mes]);

  useEffect(() => {
    const podeExportar = !loading && pessoasFiltradas.length > 0;
    setActions({
      primary: { label: 'Voltar', onClick: () => {}, href: Links.Pessoa_Index },
      ...(podeExportar && {
        secondary: {
          label: 'Excel',
          onClick: handleExportarExcel,
        },
        print: {
          label: 'Imprimir',
          onClick: handleImprimir,
        },
      }),
    });
    return () => setActions({});
  }, [setActions, handleExportarExcel, handleImprimir, loading, pessoasFiltradas.length]);

  const carregar = useCallback(async (mesSelecionado: number) => {
    setLoading(true);
    setErro(null);
    try {
      const [lista, count] = await readPerson({
        mesAniversario: mesSelecionado,
        comDefensor: true,
        comEstagiario: true,
        comColaborador: true,
        skip: 0,
        take: 1000,
      });
      setPessoas(lista);
      setTotal(count);
    } catch (e) {
      setPessoas([]);
      setTotal(0);
      setErro(e instanceof Error ? e.message : 'Falha ao carregar aniversariantes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void carregar(mes);
  }, [mes, carregar]);

  const requestSort = (key: AniversarioSortKey) => {
    setSortConfig((atual) => proximaOrdenacao(atual, key));
  };

  const toggleFiltroPerfil = (perfil: keyof FiltrosPerfilAniversario) => {
    setFiltrosPerfil((atual) => ({ ...atual, [perfil]: !atual[perfil] }));
  };

  const nenhumPerfilSelecionado = !Object.values(filtrosPerfil).some(Boolean);

  return (
    <div className="row">
      <div className="col-12 d-print-none">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-2">
          <h3 className="mb-0">Aniversários</h3>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <div className="d-flex flex-wrap align-items-center gap-2">
              {OPCOES_FILTRO_PERFIL_ANIVERSARIO.map((opcao) => (
                <Form.Check
                  key={opcao.key}
                  type="checkbox"
                  id={`filtro-perfil-${opcao.key}`}
                  className="mb-0 small"
                  label={opcao.label}
                  checked={filtrosPerfil[opcao.key]}
                  onChange={() => toggleFiltroPerfil(opcao.key)}
                />
              ))}
            </div>
            <Form.Group className="d-flex align-items-center gap-2 mb-0">
              <Form.Label className="mb-0 small text-nowrap" htmlFor="mes-aniversario">
                Mês:
              </Form.Label>
              <Form.Select
                id="mes-aniversario"
                size="sm"
                style={{ width: '10rem' }}
                value={mes}
                onChange={(e) => setMes(Number(e.target.value))}
              >
                {MESES.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        </div>
        <span className="blockquote-footer pb-4 text-end">
          Pessoas com pelo menos um perfil ativo que fazem aniversário em {nomeMes.toLowerCase()}.
        </span>
      </div>

      <hr className="pb-3 d-print-none" />

      {erro && <div className="alert alert-danger py-2 d-print-none">{erro}</div>}

      {loading ? (
        <div className="d-flex align-items-center gap-2 text-muted py-4 d-print-none">
          <Spinner animation="border" size="sm" />
          Carregando aniversariantes…
        </div>
      ) : pessoas.length === 0 ? (
        <p className="text-muted">Nenhum aniversariante encontrado neste mês.</p>
      ) : nenhumPerfilSelecionado ? (
        <p className="text-muted">Selecione ao menos um perfil para exibir aniversariantes.</p>
      ) : pessoasFiltradas.length === 0 ? (
        <p className="text-muted">Nenhum aniversariante encontrado com os perfis selecionados.</p>
      ) : (
        <div id={CONTEUDO_ID} className="col-12">
          <style>{`
            .aniversarios-grid {
              display: grid;
              grid-template-columns: 1fr;
              gap: 1rem;
              align-items: start;
            }
            @media (min-width: 992px) {
              .aniversarios-grid { grid-template-columns: 1fr 1fr; }
            }
            @media print {
              .aniversarios-grid { grid-template-columns: 1fr 1fr; gap: 0.75rem; }
              .aniversarios-grid table { font-size: 9pt; }
              .aniv-linha-passado td,
              .aniv-linha-hoje td {
                color: #000 !important;
                font-weight: normal !important;
              }
            }
            .aniv-linha-passado td {
              color: var(--bs-secondary-color, #6c757d);
            }
            .aniv-linha-hoje td {
              font-weight: 700;
            }
          `}</style>
          <p className="small text-muted mb-2 d-print-none">
            {pessoasFiltradas.length} {pessoasFiltradas.length === 1 ? 'pessoa' : 'pessoas'}
            {pessoasFiltradas.length !== total && (
              <> (de {total} no mês)</>
            )}
          </p>
          <div className="aniversarios-grid">
            <AniversariosTabela
              pessoas={colunaEsquerda}
              mesSelecionado={mes}
              sortConfig={sortConfig}
              onSort={requestSort}
            />
            <AniversariosTabela
              pessoas={colunaDireita}
              mesSelecionado={mes}
              sortConfig={sortConfig}
              onSort={requestSort}
            />
          </div>
        </div>
      )}
    </div>
  );
}
